import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Экран 10 «Терминал недоступен» — не карточка-модалка, а полноэкранная заглушка
// поверх всего интерфейса: пользоваться киоском в этот момент нельзя.
// Переводы локальные: экран показывается по сигналу Electron независимо от того,
// какой язык успел выбрать пользователь, и сам прокручивает все три.
const translations = {
  ru: {
    title: 'Терминал временно не работает',
    message: 'Проводятся технические работы. Приносим извинения — скоро всё восстановим.',
    chip: 'Сервисная служба уже в курсе',
  },
  en: {
    title: 'The terminal is temporarily out of service',
    message: 'Maintenance is in progress. Sorry for the inconvenience — we will be back shortly.',
    chip: 'The service team has been notified',
  },
  kk: {
    title: 'Терминал уақытша жұмыс істемейді',
    message: 'Техникалық жұмыстар жүргізілуде. Кешірім сұраймыз — жақын арада қалпына келтіріледі.',
    chip: 'Сервистік қызмет хабардар',
  },
};

const LANGUAGES = ['ru', 'en', 'kk'];

function ApparatNotWork({ isOpen, lang = 'ru', autoSwitchLang = true, message }) {
  const [currentLang, setCurrentLang] = useState(lang);

  // Автоматическая смена языка, если включена
  useEffect(() => {
    if (!autoSwitchLang) return;

    const interval = setInterval(() => {
      setCurrentLang((prevLang) => {
        const currentIndex = LANGUAGES.indexOf(prevLang);
        return LANGUAGES[(currentIndex + 1) % LANGUAGES.length];
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [autoSwitchLang]);

  // Обновление языка при изменении пропса lang
  useEffect(() => {
    setCurrentLang(lang);
  }, [lang]);

  const text = translations[currentLang] || translations.ru;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 bg-[#E9EAEE] flex items-center justify-center px-[2.4vw] py-[2vh]"
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="w-full max-w-[86vw] bg-white rounded-[4vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[5vw] py-[5vh] flex flex-col items-center text-center"
          >
            <div className="relative w-[15vh] h-[15vh] rounded-full bg-[#F7EEDC] flex items-center justify-center mb-[3.4vh]">
              {/* расходящееся кольцо — «идут работы», а не статичная ошибка */}
              <span
                className="absolute -inset-[1.2vh] rounded-full border-[0.3vh] border-[#E7D4AE]"
                style={{ animation: 'ringPulse 2.6s ease-out infinite' }}
              />
              <svg
                className="w-[7.2vh] h-[7.2vh]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B07B22"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.7 6.3a4 4 0 0 0 5 5l-9.6 9.6a2.5 2.5 0 0 1-3.5-3.5z" />
                <path d="M14.7 6.3l3-3a4 4 0 0 1 4 4l-3 3" />
              </svg>
            </div>

            <motion.h1
              key={`title-${currentLang}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="text-[3.8vh] font-extrabold tracking-tight leading-tight max-w-[62vw]"
            >
              {text.title}
            </motion.h1>

            <motion.p
              key={`msg-${currentLang}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              className="text-[2.1vh] font-semibold text-[#7B8290] mt-[1.8vh] max-w-[62vw] leading-snug"
            >
              {/* message приходит из Electron-сигнала lock-screen, если задан */}
              {message || text.message}
            </motion.p>

            <span className="mt-[2.8vh] bg-[#F7EEDC] text-[#8A6015] rounded-full px-[2.4vw] py-[1vh] text-[1.6vh] font-bold">
              {text.chip}
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ApparatNotWork;
